/**
 * Overlay Images セクション — PNG を 4 スロットで読み込むブロック。
 * Phase F Step 4（Surface / Inner / Background の 3 スロット）→
 * Phase J D で **Foreground** を追加して 4 スロット化。
 *
 * state 層は `state.overlays = { surface, inner, background, foreground }` に
 * 統一されており、本 UI は各スロットに対して同じ UI パターンを展開する。
 *
 * 構成（COLORS セクションの直後に配置。**上が前面・下が背面**の
 * 直感順で Foreground → Surface → Inner → Background の 4 行）:
 *
 *   OVERLAY IMAGES
 *     Foreground   [Load PNG...] [Clear]   ← 最前面（Phase J 追加）
 *     filename or (empty)
 *     Surface      [Load PNG...] [Clear]
 *     filename or (empty)
 *     Inner        [Load PNG...] [Clear]
 *     filename or (empty)
 *     Background   [Load PNG...] [Clear]
 *     filename or (empty)
 *
 * 挙動（各スロット共通）:
 * 1. Load PNG ボタン → hidden な `<input type="file" accept="image/png">`
 *    の `.click()` を発火。
 * 2. change → FileReader.readAsDataURL → Image オブジェクト作成 →
 *    image.onload で `store.set({ overlays: { ...prev, [slotKey]: {...} } })`。
 * 3. 読み込み失敗は `console.error` のみ（UI には表示しない簡易版）。
 * 4. Clear ボタン → 該当スロットのみを `null` に戻し、他スロットは保持。
 *
 * ファイル名は state の `OverlayImage.filename` から読む。closure に
 * 保持しないため、外部から store を書き換えてもそのまま UI に反映される。
 */

import type { AppState, Store } from '../../state';

/** 4 スロットの識別子。`AppState.overlays` のキーと 1:1 対応。 */
type OverlaySlotKey = 'surface' | 'inner' | 'background' | 'foreground';

/**
 * Overlay Images セクションをマウントする。
 *
 * @param root - 挿入先コンテナ（通常は `.controls` 配下の新規 `<div>`）
 * @param store - グローバルストア
 * @returns teardown 関数。呼ぶと DOM・subscribe を解除する。
 */
export function mountOverlayImage(root: HTMLElement, store: Store<AppState>): () => void {
  // ── section 本体 ──────────────────────────────────────────
  const section = document.createElement('div');

  const label = document.createElement('div');
  label.className = 'section-label';
  label.textContent = 'Overlay Images';
  section.appendChild(label);

  // 4 スロットを同じパターンで並べる（上が前面・下が背面の直感順）。
  // 各スロットの teardown を集めて最後にまとめて呼ぶ（DOM と subscribe の
  // 両方を解放する）。
  const teardowns: Array<() => void> = [];
  teardowns.push(mountOverlaySlot(section, store, 'foreground', 'Foreground'));
  teardowns.push(mountOverlaySlot(section, store, 'surface', 'Surface'));
  teardowns.push(mountOverlaySlot(section, store, 'inner', 'Inner'));
  teardowns.push(mountOverlaySlot(section, store, 'background', 'Background'));

  root.appendChild(section);

  return (): void => {
    for (const fn of teardowns) fn();
    section.remove();
  };
}

/**
 * 1 スロット分の UI（ラベル + Load/Clear + filename）を section に
 * append する。state 書き換えは `overlays` object の shallow clone で
 * 該当キーだけ差し替える形にし、他スロットを誤って潰さない。
 */
function mountOverlaySlot(
  section: HTMLElement,
  store: Store<AppState>,
  slotKey: OverlaySlotKey,
  labelText: string,
): () => void {
  // ── slot コンテナ（label + buttons + filename を縦積み） ──
  const slot = document.createElement('div');
  slot.className = 'overlay-slot';

  // ── 1 段目: slot label + Load/Clear ボタン並び ────────────
  const header = document.createElement('div');
  header.className = 'overlay-slot-header';

  const slotLabel = document.createElement('div');
  slotLabel.className = 'overlay-slot-label';
  slotLabel.textContent = labelText;
  header.appendChild(slotLabel);

  // hidden file input（PNG 限定）。slot ごとに独立させないと複数スロットで
  // 干渉する（change event が最後の input に吸われる）のでスロット内部に置く。
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/png';
  fileInput.style.display = 'none';
  header.appendChild(fileInput);

  const loadBtn = document.createElement('button');
  loadBtn.type = 'button';
  loadBtn.className = 'btn overlay-slot-btn';
  loadBtn.textContent = 'Load PNG...';
  header.appendChild(loadBtn);

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'btn ghost overlay-slot-btn';
  clearBtn.textContent = 'Clear';
  header.appendChild(clearBtn);

  slot.appendChild(header);

  // ── 2 段目: ファイル名表示（読込済なら filename、未読なら `(empty)`）
  const nameDisplay = document.createElement('div');
  nameDisplay.className = 'overlay-filename';
  slot.appendChild(nameDisplay);

  /** UI 状態を state から同期する（subscribe と初期表示で共通）。 */
  const sync = (): void => {
    const entry = store.get().overlays[slotKey];
    clearBtn.disabled = entry === null;
    nameDisplay.textContent = entry !== null ? entry.filename : '(empty)';
  };

  sync();

  // ── Load クリック → file input を開く ──────────────────────
  const onLoadClick = (): void => {
    fileInput.click();
  };

  // ── file input change → DataURL → Image → store.set ──────
  const onFileChange = (): void => {
    const file = fileInput.files?.[0];
    if (!file) return;

    const fileName = file.name;
    const reader = new FileReader();

    reader.onload = (): void => {
      const result = reader.result;
      if (typeof result !== 'string') {
        // readAsDataURL は string を返すはずだが念のため。
        console.error(`[overlayImage:${slotKey}] FileReader result is not a string`);
        return;
      }
      const img = new Image();
      img.onload = (): void => {
        // 他スロットを巻き込まないよう shallow clone してから差し替える。
        const prev = store.get().overlays;
        store.set({
          overlays: {
            ...prev,
            [slotKey]: { image: img, dataUrl: result, filename: fileName },
          },
        });
      };
      img.onerror = (): void => {
        console.error(`[overlayImage:${slotKey}] failed to decode image:`, fileName);
      };
      img.src = result;
    };

    reader.onerror = (): void => {
      console.error(`[overlayImage:${slotKey}] FileReader error:`, reader.error);
    };

    reader.readAsDataURL(file);

    // 同じファイルを再選択できるよう input を空に戻す
    // （値は読み取り済みなので問題ない）。
    fileInput.value = '';
  };

  // ── Clear クリック → 該当スロットだけ null に戻す ─────────
  const onClearClick = (): void => {
    const prev = store.get().overlays;
    if (prev[slotKey] === null) return;
    store.set({
      overlays: {
        ...prev,
        [slotKey]: null,
      },
    });
  };

  loadBtn.addEventListener('click', onLoadClick);
  fileInput.addEventListener('change', onFileChange);
  clearBtn.addEventListener('click', onClearClick);

  // overlays[slotKey] の参照変更（内外いずれの経路でも）に追従して UI を同期。
  // overlays object 全体を見て、自スロットの参照が変わったときだけ sync する。
  const unsubscribe = store.subscribe((state, prev) => {
    if (state.overlays[slotKey] !== prev.overlays[slotKey]) {
      sync();
    }
  });

  section.appendChild(slot);

  return (): void => {
    unsubscribe();
    loadBtn.removeEventListener('click', onLoadClick);
    fileInput.removeEventListener('change', onFileChange);
    clearBtn.removeEventListener('click', onClearClick);
    slot.remove();
  };
}
